import { isArray } from 'lodash'
import moment from 'moment'

import { augmentIncomingCarouselCards } from 'api/models'
import { augmentIncomingCreativeMedia } from '../../Media/mediaPayload'
import { derivePlatformSelectionFromSetup, platformIncludesMeta } from '../../platform'

import { creativeActions } from './creativeFormReducer'
import i18n from 'i18n'

/**
 * Returns the removed fields based on the diff of two versions of form contents.
 * This is used to offset the disabled variations when a field is removed.
 *
 * @param {Object} a - The first version of the form contents.
 * @param {Object} b - The second version of the form contents.
 * @param {Object} action - The action object.
 * @returns {Object|null} - The removed fields or null if no removed fields are found.
 */
export const getRemovedField = (a, b, action) => {
  if (typeof action.index === 'undefined' || action.index === null) {
    return null
  }

  const typeMap = {
    [creativeActions.bodyUpdate]: 'body',
    [creativeActions.headlineUpdate]: 'headline',
    [creativeActions.mediaUpdate]: 'media',
    [creativeActions.mediaAndPlacementUpdate]: 'media',
    [creativeActions.destinationUrlUpdate]: 'destination_url',
  }
  for (const key in a) {
    try {
      if (
        isArray(a[key]) &&
        b[key].filter(Boolean).length < a[key].filter(Boolean).length &&
        ['body', 'headline', 'media', 'destination_url'].includes(key)
      ) {
        return { type: typeMap[action.type], index: action.index }
      }
    } catch (e) {
      console.error('diff error', key, a[key], b[key])
    }
  }

  return null
}
export function parseCreatives(setup, tc, eventDate, hasPrismaAccess) {
  const originalCreatives = setup?.creatives.map((creative) => {
    // YouTube videos are stored as entries in the flat media[] list (see
    // mapYoutubeVideoForPayload) and rendered as regular MediaUpload cards —
    // treated as a regular video for limit purposes (counts toward the
    // shared media cap), so more than one is allowed — no artificial "1
    // YouTube video max" restriction.
    const mergedMedia = augmentIncomingCreativeMedia({
      media: creative.media ?? [],
      placement_media: creative.placement_media ?? [],
    })

    return {
      ...creative,
      headline: [...(creative.headline ?? [])],
      body: [...(creative.body ?? [])],
      media: mergedMedia,
      placement_media: [],
      destination_url: [...(creative.destination_url ?? [])],
      carousel_cards: augmentIncomingCarouselCards(creative.carousel_cards || []),
      call_to_action: creative.call_to_action,
    }
  })

  const creative = originalCreatives?.find((x) => x.tc === tc) ?? {}
  const [descriptionDate, descriptionTime] = moment
    .utc(eventDate)
    .format('DD.MM.YYYY HH:mm')
    .split(' ')

  // description is a Meta-only field (campaign.js's validateCreative,
  // CampaignForm.jsx's DescriptionField) — the default templated value below
  // must not be filled in for a Google-only setup, same as the pruning
  // CampaignSetup.jsx's writeThroughPlatformAndCampaignType already does on
  // a platform switch.
  const isGoogleOnly = !platformIncludesMeta(derivePlatformSelectionFromSetup(setup))

  return {
    ...creative,
    headline: creative.headline ?? [],
    body: creative.body ?? [],
    media: creative.media ?? [],
    placement_media: creative.placement_media ?? [],
    destination_url: creative.destination_url ?? [],
    carousel_cards: creative.carousel_cards ?? [],
    call_to_action: creative.call_to_action,
    description:
      creative.description ||
      (hasPrismaAccess || isGoogleOnly
        ? ''
        : i18n.t('Recommendations.campaign.setup.description.default', {
          date: descriptionDate,
          time: descriptionTime,
        })),
  }
}
